import { formatAppDateTime } from "../../app/utils/appTimeFormat";

const TYPE_LABELS = {
  Deposit: "Deposit",
  Withdrawal: "Withdrawal",
  Betting: "P2P stake",
  WinBet: "P2P win",
  LoseBet: "P2P loss",
  CancelBet: "P2P cancel",
  CopyBet: "Copy bet",
  CopyBetFee: "Copy bet fee",
  CopyBetReward: "Copy bet reward",
  CreateBettingHouse: "Create bet house",
  EditBettingHouse: "Edit bet house",
  CreateP2P: "Create P2P",
  CreatePolymarket: "Create market",
  CreateCasino: "Create casino",
  BonusCredit: "Bonus credit",
  LeaderboardBonus: "Leaderboard bonus",
  AdminReward: "Admin reward",
  PlatformFee: "Platform fee",
  Commission: "Commission",
  CommissionFee: "Commission fee",
  Transfer: "Transfer",
  Conversion: "Conversion",
  Tip: "Tip",
  Reversal: "Reversal",
  Refund: "Refund",
  CasinoPlayerWin: "Casino win",
  CasinoPlayerLose: "Casino loss",
  BondWheelStake: "Bond wheel stake",
  BondWheelPlayerPayout: "Bond wheel payout",
};

export const TX_STATUS_META = {
  Success: { label: "Success", tone: "live" },
  Pending: { label: "Pending", tone: "warn" },
  Hold: { label: "On hold", tone: "warn" },
  Failed: { label: "Failed", tone: "muted" },
  Cancelled: { label: "Cancelled", tone: "muted" },
};

export function transactionTypeLabel(type) {
  if (!type) return "Transaction";
  return TYPE_LABELS[type] || String(type).replace(/([a-z])([A-Z])/g, "$1 $2");
}

export function transactionStatusMeta(status) {
  return (
    TX_STATUS_META[status] || {
      label: status || "—",
      tone: "muted",
    }
  );
}

export function transactionInitials(tx) {
  const cur = String(tx?.currency || "CA").slice(0, 2);
  return cur.toUpperCase();
}

export function formatTransactionAt(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
}

export function shortTransactionRef(ref) {
  const src = String(ref || "").trim();
  if (!src) return null;
  if (src.length <= 12) return src;
  return `${src.slice(0, 8)}…`;
}

/** Compact ledger subline (member + amount + ref + method). */
export function transactionLedgerSubline(tx, formatCompact) {
  const parts = [];
  if (tx.userName) parts.push(tx.userName);
  const amount = Number(tx.amount) || 0;
  const currency = String(tx.currency || "CASH");
  parts.push(`${formatCompact(amount)} ${currency}`);
  const ref = shortTransactionRef(tx.transactionRef);
  if (ref) parts.push(ref);
  if (tx.method && tx.method !== "Internal") parts.push(tx.method);
  return parts.length ? parts.join(" · ") : "—";
}

export function transactionContextLine(tx) {
  const parts = [formatTransactionAt(tx.createdAt)];

  const ref = shortTransactionRef(tx.transactionRef);
  if (ref) parts.push(ref);

  if (tx.method && tx.method !== "Internal") {
    parts.push(tx.method);
  }

  if (tx.description) {
    const desc = String(tx.description).trim();
    if (desc) parts.push(desc.length > 48 ? `${desc.slice(0, 48)}…` : desc);
  }

  return parts.join(" · ");
}

export function transactionMetricsLine(tx, formatCompact) {
  const amount = Number(tx.amount) || 0;
  const currency = String(tx.currency || "CASH");
  const balanceAfter = Number(tx.balanceAfter);

  const parts = [`${formatCompact(amount)} ${currency}`];
  if (Number.isFinite(balanceAfter)) {
    parts.push(`${formatCompact(balanceAfter)} bal`);
  }

  return parts.join(" · ");
}

export function transactionSummaryParts(summary) {
  if (!summary) return [];
  const parts = [];
  if (summary.pendingCount) parts.push(`${summary.pendingCount} pending`);
  if (summary.onHoldCount) parts.push(`${summary.onHoldCount} on hold`);
  if (summary.failedCount) parts.push(`${summary.failedCount} failed`);
  return parts;
}

export function transactionDeskSummaryParts(summary) {
  if (!summary) return [];
  const parts = [];
  if (summary.pendingDeposits) parts.push(`${summary.pendingDeposits} dep pending`);
  if (summary.pendingWithdrawals) parts.push(`${summary.pendingWithdrawals} wd pending`);
  if (summary.hold) parts.push(`${summary.hold} on hold`);
  return parts;
}
